import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Button,
    Alert,
    CircularProgress,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { getClinic, putClinic, testSmsAero } from "../http/clinicAPI";
import useSnackbar from "../hooks/useSnackbar";
import MySnackbar from "./MySnackbar";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";

const SmsAeroSettings = () => {
    const snack = useSnackbar(false, "Ошибка");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const [smsAeroEnabled, setSmsAeroEnabled] = useState(false);
    const [smsAeroConfig, setSmsAeroConfig] = useState({
        email: "",
        apiKey: "",
        sign: "",
    });

    const [errors, setErrors] = useState({
        email: "",
        apiKey: "",
    });

    useEffect(() => {
        fetchClinicData();
    }, []);

    const fetchClinicData = async () => {
        try {
            const data = await getClinic();
            const smsAeroAuth = data.systemSecrets?.smsAeroAuth;
            
            if (smsAeroAuth) {
                setSmsAeroEnabled(true);
                setSmsAeroConfig({
                    email: smsAeroAuth.email || "",
                    apiKey: smsAeroAuth.apiKey || "",
                    sign: smsAeroAuth.sign || "",
                });
            }
        } catch (error) {
            snack.handleChange(true, "Ошибка при загрузке данных клиники");
        } finally {
            setLoading(false);
        }
    };

    const validate = () => {
        const newErrors = { email: "", apiKey: "" };
        let isValid = true;

        if (!smsAeroEnabled) return true;

        if (!smsAeroConfig.email) {
            newErrors.email = "Email обязателен";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(smsAeroConfig.email)) {
            newErrors.email = "Некорректный email";
            isValid = false;
        }

        if (!smsAeroConfig.apiKey) {
            newErrors.apiKey = "API Key обязателен";
            isValid = false;
        } else if (smsAeroConfig.apiKey.length < 10) {
            newErrors.apiKey = "API Key должен быть не короче 10 символов";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSave = async () => {
        if (!validate()) {
            return;
        }

        setSaving(true);
        try {
            const clinicData = await getClinic();
            const currentSystemSecrets = clinicData.systemSecrets || {};

            const newSystemSecrets = {
                ...currentSystemSecrets,
                smsAeroAuth: smsAeroEnabled
                    ? {
                          email: smsAeroConfig.email.trim(),
                          apiKey: smsAeroConfig.apiKey.trim(),
                          sign: smsAeroConfig.sign?.trim() || undefined,
                      }
                    : undefined,
            };

            const formData = new FormData();
            formData.append("systemSecrets", JSON.stringify(newSystemSecrets));

            await putClinic(formData);
            snack.handleChange(true, "Настройки SMS Aero сохранены", "success");
        } catch (error) {
            snack.handleChange(true, "Ошибка при сохранении настроек SMS Aero");
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        if (!validate()) {
            return;
        }

        setTesting(true);
        try {
            const data = await testSmsAero();

            snack.handleChange(true, data.message || 'Тестовое SMS отправлено успешно', 'success');
        } catch (error) {
            console.error('Ошибка при тестовой отправке:', error);
            snack.handleChange(true, error.message || error.error || 'Ошибка при тестовой отправке');
        } finally {
            setTesting(false);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setSmsAeroConfig((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Очистить ошибку при вводе
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <MySnackbar {...snack} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
                Настройки SMS Aero
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    SMS Aero используется как резервный канал отправки сообщений, 
                    если сообщение в Telegram не было доставлено. Пользователь получит SMS 
                    на указанный номер телефона.
                </Typography>
            </Alert>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={smsAeroEnabled}
                        onChange={(e) => setSmsAeroEnabled(e.target.checked)}
                    />
                }
                label="Включить SMS-рассылки как резервный канал"
                sx={{ mb: 2 }}
            />

            {smsAeroEnabled && (
                <Box sx={{ ml: 2, mb: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            label="Email от SMS Aero"
                            name="email"
                            value={smsAeroConfig.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            fullWidth
                            required
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            margin="normal"
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <TextField
                            label="API Key"
                            name="apiKey"
                            value={smsAeroConfig.apiKey}
                            onChange={handleChange}
                            error={!!errors.apiKey}
                            helperText={errors.apiKey}
                            fullWidth
                            required
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            margin="normal"
                        />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <TextField
                            label="Подпись отправителя (опционально)"
                            name="sign"
                            value={smsAeroConfig.sign}
                            onChange={handleChange}
                            fullWidth
                            variant="outlined"
                            placeholder="TolaMed"
                            InputLabelProps={{ shrink: true }}
                            margin="normal"
                            InputProps={{
                                endAdornment: (
                                    <Tooltip 
                                        title="Имя отправителя SMS. Если не указано, используется дефолтная подпись"
                                        arrow
                                    >
                                        <InfoIcon 
                                            sx={{ opacity: 0.5, cursor: "help" }} 
                                            fontSize="small" 
                                        />
                                    </Tooltip>
                                ),
                            }}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={handleTest}
                            disabled={testing || !smsAeroConfig.email || !smsAeroConfig.apiKey}
                            startIcon={testing ? <CircularProgress size={16} /> : null}
                        >
                            {testing ? "Отправка..." : "Тестовая отправка"}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                            {saving ? "Сохранение..." : "Сохранить настройки"}
                        </Button>
                    </Box>
                </Box>
            )}

            {!smsAeroEnabled && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        SMS-рассылки отключены. Пользователи не получат сообщения, 
                        если Telegram недоступен.
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default SmsAeroSettings;